import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useReactMediaRecorder } from '@src/hooks/useMediaRecorder';
import { Alert, Button } from 'rsuite';
import ViewFinder from '@src/components/CameraRecord/ViewFinder';
import {
  clearUploadedVideo,
  completeRecording,
  getShouldShowCameraRecorder,
  hideCameraRecorder,
  selectVideo,
  getCampaignBanner,
  getUseCampaignBanner,
  getCampaignBrandFontColor,
  getCampaignLanguage,
  getCampaignElementById,
} from '@src/redux/modules/campaignLandingPage';
import styles from '@src/components/CameraRecord/CameraRecord.module.less';
import { StyledBrandingButton, StyledOutlineBrandingButton } from '@src/components/CampaignLanding/styles';
import { getTranslation } from '@src/translations/dictionary';

const CameraRecord = ({
  _clearUploadedVideo,
  _completeRecording,
  _hideCameraRecorder,
  _selectVideo,
  qid,
  shouldShowCameraRecorder,
  backgroundColor = '',
  color = '',
  useBanner = false,
  language,
  promptText,
}) => {
  if (!shouldShowCameraRecorder) {
    return null;
  }

  const startRecordingLabel = getTranslation('start_recording_label', language);
  const stopRecordingLabel = getTranslation('stop_recording_label', language);
  const cancelRecordingLabel = getTranslation('cancel_label', language);

  const { status, startRecording, stopRecording, mediaBlobUrl, previewStream, error } =
    useReactMediaRecorder({
      audio: true,
      video: {
        facingMode: 'user',
        width: 1920,
        height: 1080
      }
    });

  useEffect(() => {
    // if the object URL has updated,
    // dispatch it to the store
    // this will cause the VideoPreview
    // component to render with the mediaBlobUrl
    // as the source
    _selectVideo(qid, mediaBlobUrl);

    // If we have a blob url, hide the camera recorder
    if (mediaBlobUrl !== null) {
      _hideCameraRecorder(qid);
    }
  }, [mediaBlobUrl]);

  useEffect(() => {
    if (error === 'empty_recording') {
      const errorMessage = getTranslation('recording_failed_message', language)
        || 'Recording failed. Please try again.';
      Alert.error(errorMessage, 7500);
      setIsRecording(false);
    }
  }, [error, language]);

  const [isCountingDown, setIsCountingDown] = useState(false);

  // Keep track of if we're recording
  const [isRecording, setIsRecording] = useState(false);

  // Function to initiate recording
  const handleStartRecording = () => {
    setIsCountingDown(true);
    // Wait three seconds (the time of the countdown)
    // until we actually start recording
    const wait = new Promise((resolve) => {
      setTimeout(() => {
        resolve();
      }, 3000);
    });

    wait.then(() => {
      setIsCountingDown(false);
      startRecording();
      setIsRecording(true);

      // Dispatch a null object URL just in case the
      // user is recording a video to replace their
      // last one
      _selectVideo(qid, null);
    });
  };

  const handleStopRecording = () => {
    stopRecording();
    setIsRecording(false);
    _completeRecording(qid);
  };

  const handleCancelRecording = () => {
    _hideCameraRecorder(qid);
  };

  const useCustomeStyle = useBanner && backgroundColor && color;

  return (
    <section className={styles.vpCameraRecordSection}>
      <ViewFinder isCountingDown={isCountingDown} status={status} stream={previewStream} promptText={promptText} />

      <section className={styles.vpCameraButtonsContainer}>
        {!isRecording &&
          mediaBlobUrl === null && ( // idle and ready to record
            <StyledBrandingButton
              variant="contained"
              backgroundColor={backgroundColor}
              fontColor={color}
              custom={useCustomeStyle}
              className={styles.vpStartRecordingButton}
              onClick={() => {
                _clearUploadedVideo(qid);
                handleStartRecording();
              }}
            >
              {startRecordingLabel}
            </StyledBrandingButton>
          )}

        {!isRecording &&
          mediaBlobUrl === null && (
            <StyledOutlineBrandingButton
              variant="outlined"
              backgroundColor={backgroundColor}
              fontColor={color}
              custom={useCustomeStyle}
              className={styles.vpStartRecordingButton}
              onClick={() => {
                handleCancelRecording();
              }}
            >
              {cancelRecordingLabel}
            </StyledOutlineBrandingButton>
          )}

        {isRecording && ( // currently recording
          <Button
            appearance="ghost"
            className={styles.vpStopRecordingButton}
            onClick={() => {
              handleStopRecording();
            }}
          >
            {stopRecordingLabel}
          </Button>
        )}
      </section>
    </section>
  );
};

CameraRecord.propTypes = {
  _clearUploadedVideo: PropTypes.func,
  _completeRecording: PropTypes.func,
  _hideCameraRecorder: PropTypes.func,
  _selectVideo: PropTypes.func,
  qid: PropTypes.string,
  shouldShowCameraRecorder: PropTypes.bool,
  urlType: PropTypes.string,
  backgroundColor: PropTypes.string,
  color: PropTypes.string,
  useBanner: PropTypes.bool,
  language: PropTypes.string,
  promptText: PropTypes.string,
};

const mapStateToProps = (state, ownProps) => ({
  shouldShowCameraRecorder: getShouldShowCameraRecorder(state, ownProps.qid),
  backgroundColor: getCampaignBanner(state),
  useBanner: getUseCampaignBanner(state),
  color: getCampaignBrandFontColor(state),
  language: getCampaignLanguage(state),
  promptText: getCampaignElementById(state, ownProps.qid)?.description || '',
});

const mapDispatchToProps = {
  _clearUploadedVideo: clearUploadedVideo,
  _completeRecording: completeRecording,
  _hideCameraRecorder: hideCameraRecorder,
  _selectVideo: selectVideo
};

export default connect(mapStateToProps, mapDispatchToProps)(CameraRecord);



// WEBPACK FOOTER //
// ./src/components/CameraRecord/CameraRecord.jsx